import _CalendarGroup from 'owa-service/lib/contract/CalendarGroup';
import CalendarFolder from 'owa-service/lib/contract/CalendarFolder';
import CalendarFolderTypeEnum from 'owa-service/lib/contract/CalendarFolderTypeEnum';
import {
    getCalculatedFolderId,
    getCalendarGroupKey,
    Calendar,
    CalendarEntryExtended,
    CalendarGroupExtended,
    CalendarId,
    CalendarCacheStoreData,
} from 'owa-calendar-cache';
import { getUserMailboxInfo } from 'owa-client-ids';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { shouldAddCalendarToCache } from './shouldAddCalendarToCache';

export function convertToCalendarCacheStoreData(
    groups: _CalendarGroup[],
    folders: CalendarFolder[],
    userIdentity?: string
): CalendarCacheStoreData {
    let defaultCalendar: Calendar;
    let calendarEntryMapping: { [key: string]: CalendarEntryExtended } = {};
    let calendarFolderMapping: { [key: string]: CalendarFolder } = {};
    let folderIdMapping: { [key: string]: CalendarId } = {};
    let calendarGroupMapping: { [key: string]: CalendarGroupExtended } = {};
    let calendarIdToChangeKeyMapping: { [key: string]: string } = {};
    let calendarKeys: string[] = userIdentity
        ? [userIdentity]
        : [getUserConfiguration().SessionSettings.LogonEmailAddress];

    const userMailboxInfo = getUserMailboxInfo(userIdentity);

    groups.forEach((group: _CalendarGroup) => {
        calendarIdToChangeKeyMapping[group.ItemId.Id] = group.ItemId.ChangeKey;

        let groupEx = {
            ...group,
            id: { id: group.GroupId, mailboxInfo: userMailboxInfo },
        };

        const groupKey = getCalendarGroupKey(userMailboxInfo.userIdentity, group.GroupId);
        calendarGroupMapping[groupKey] = groupEx;
        group.Calendars.forEach(calendarEntry => {
            if (shouldAddCalendarToCache(calendarEntry, calendarKeys)) {
                let entryId = calendarEntry.ItemId.Id;
                let calcFolderId = getCalculatedFolderId(calendarEntry).Id;

                let calendarEntryEx = {
                    ...calendarEntry,
                    id: { id: entryId, mailboxInfo: userMailboxInfo },
                };

                if (calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.DefaultCalendar) {
                    // the default calendar entry from getCalendarGroups displayName is not localized correctly,
                    // so we pull the name from the getCalendarFolders response

                    const defaultFolder =
                        folders && folders.filter(folder => folder.FolderId.Id === calcFolderId)[0];

                    defaultCalendar = {
                        id: { id: entryId, mailboxInfo: userMailboxInfo },
                        calculatedFolderId: calcFolderId,
                        displayName: defaultFolder
                            ? defaultFolder.DisplayName
                            : calendarEntry.CalendarName,
                        colorScheme: null,
                    };

                    calendarEntryEx.CalendarName = defaultCalendar.displayName;
                }

                // Do not override any existent mapping.
                // One case it protects for is when user shares its own calendar with himself.
                !calendarEntryMapping[entryId] && (calendarEntryMapping[entryId] = calendarEntryEx);
                !folderIdMapping[calcFolderId] &&
                    (folderIdMapping[calcFolderId] = {
                        id: calendarEntry.ItemId.Id,
                        mailboxInfo: userMailboxInfo,
                    });

                !calendarIdToChangeKeyMapping[entryId] &&
                    (calendarIdToChangeKeyMapping[entryId] = calendarEntry.ItemId.ChangeKey);
            }
        });
    });

    folders.forEach(folder => {
        // As we are not supporting all calendars for now in client e.g. Group, schedule etc.
        // so for a particular folder there will be no corresponding mapping to calendar.
        // Hence we check if there is a corresponding calendar for a folder, only then
        // add the mapping from calendar to that folder.
        const calendarId = folderIdMapping[folder.FolderId.Id];

        if (calendarId) {
            calendarFolderMapping[calendarId.id] = folder;
        }
    });

    return {
        defaultCalendar: defaultCalendar,
        calendarEntryMapping: calendarEntryMapping,
        calendarFolderMapping: calendarFolderMapping,
        folderIdMapping: folderIdMapping,
        calendarGroupMapping: calendarGroupMapping,
        calendarIdToChangeKeyMapping: calendarIdToChangeKeyMapping,
    };
}
